import dayjs from 'dayjs'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'
import * as Yup from 'yup'

import { useMutation, useQuery, useQueryList } from '@percent/utility'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  Button,
  DateInput,
  Divider,
  Flex,
  FormField,
  Modal,
  Select,
  Text,
  TextArea,
  TextInput
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { TaskTemplate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import type { EditTaskTemplateModalProps } from './EditTaskTemplateModal.types'

type FormValues = {
  name: string
  description: string
  dueByDate: Date | null | undefined
  hoursUntilDue: number | null | undefined
  expiresAt: Date | null | undefined
  details: {
    type: 'review' | 'decision' | 'completion_event' | 'bank_details'
    formId?: string
    completionEventName?: string
  }
  audience: 'partner' | 'organisation'
}

type Option<T> = { label: string; value: T }

const deleteNullValues = (obj: any): any => {
  Object.keys(obj).forEach(key => {
    if (obj[key] && typeof obj[key] === 'object') {
      deleteNullValues(obj[key])
    } else if (obj[key] === null || obj[key] === undefined) {
      // eslint-disable-next-line no-param-reassign
      delete obj[key]
    }
  })

  return obj
}

export function EditTaskTemplateModal({
  taskTemplate,
  programId,
  isOpen,
  onClose,
  refresh,
  onPreviewForm
}: EditTaskTemplateModalProps) {
  const { t } = useTranslation()
  const { grantProgramService, adminService } = useServices()

  const [selectedPartnerId, setSelectedPartnerId] = useState('')
  const [selectedPartnerLabel, setSelectedPartnerLabel] = useState('')
  const [partnerSearchQuery, setPartnerSearchQuery] = useState('')
  const [debouncedPartnerQuery] = useDebounceValue(partnerSearchQuery, 300)

  const [{ data: partnerListData, isLoading: isPartnersLoading }] = useQueryList(
    ['partners', 'task-template', debouncedPartnerQuery],
    adminService.queries.getPartnerList,
    { query: debouncedPartnerQuery, pageSize: 20 }
  )

  useEffect(() => {
    if (isOpen) setPartnerSearchQuery('')
  }, [isOpen])

  const allFormsLabel = t('dialog.addTaskTemplate.form.allForms')
  const partnerSelectOptions = [
    { label: allFormsLabel, value: '' },
    ...(Array.isArray(partnerListData) ? partnerListData : []).map((p: { id: string; name: string }) => ({
      label: p.name,
      value: p.id
    }))
  ]

  const formsQueryParams = selectedPartnerId ? { partnerId: selectedPartnerId } : {}
  const { data: formsData, isLoading: formsLoading } = useQuery(
    ['forms', selectedPartnerId],
    grantProgramService.getForms,
    formsQueryParams
  )
  const forms = Array.isArray(formsData) ? formsData : []

  const templateTypes: Option<NonNullable<TaskTemplate['details']>['type']>[] = [
    { label: t('dialog.addTaskTemplate.form.type.review'), value: 'review' },
    {
      label: t('dialog.addTaskTemplate.form.type.decision'),
      value: 'decision'
    },
    {
      label: t('dialog.addTaskTemplate.form.type.completionEvent'),
      value: 'completion_event'
    },
    {
      label: t('dialog.addTaskTemplate.form.type.bankDetails'),
      value: 'bank_details'
    }
  ]

  const audienceTypes: Option<NonNullable<TaskTemplate['audience']>>[] = [
    {
      label: t('dialog.addTaskTemplate.form.audience.partner'),
      value: 'partner'
    },
    {
      label: t('dialog.addTaskTemplate.form.audience.organisation'),
      value: 'organisation'
    }
  ]

  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)

  const deadlineType = taskTemplate.dueByDate ? 'dueByDate' : 'hoursUntilDue'

  const validationSchema = useMemo(
    () =>
      Yup.object()
        .shape({
          name: Yup.string()
            .max(
              255,
              t('errorMessage.orgFieldCharacterLimit', {
                fieldName: 'Template name'
              })
            )
            .min(
              1,
              t('errorMessage.orgFieldCharacterLimit', {
                fieldName: 'Template name'
              })
            )
            .trim()
            .required(t('errorMessage.required')),
          description: Yup.string()
            .max(
              255,
              t('errorMessage.orgFieldCharacterLimit', {
                fieldName: 'Description'
              })
            )
            .min(
              1,
              t('errorMessage.orgFieldCharacterLimit', {
                fieldName: 'Description'
              })
            )
            .trim()
            .required(t('errorMessage.required')),
          dueByDate: Yup.date().optional().nullable(),
          expiresAt: Yup.date().optional().nullable(),
          hoursUntilDue: Yup.number()
            .nullable()
            .when('dueByDate', {
              is: (dueByDate: Date | null) => !dueByDate,
              then: schema => schema.required(t('errorMessage.required')),
              otherwise: schema => schema.optional()
            }),
          details: Yup.object()
            .shape({
              type: Yup.string().oneOf(['review', 'decision', 'completion_event', 'bank_details']).required(),
              formId: Yup.string().when('type', {
                is: (type: string) => type === 'review' || type === 'decision',
                then: Yup.string().required()
              }),
              completionEventName: Yup.string().when('type', {
                is: 'completion_event',
                then: Yup.string().required()
              })
            })
            .noUnknown(),
          audience: Yup.string().oneOf(['partner', 'organisation']).required()
        })
        .transform(value => deleteNullValues(value)),
    [t]
  )

  const initialValues = useMemo<FormValues>(
    () => ({
      name: taskTemplate.name,
      description: taskTemplate.description || '',
      dueByDate: taskTemplate.dueByDate ? new Date(taskTemplate.dueByDate) : null,
      hoursUntilDue: taskTemplate.hoursUntilDue ? Number(taskTemplate.hoursUntilDue) : null,
      expiresAt: taskTemplate.expiresAt ? new Date(taskTemplate.expiresAt) : null,
      details: taskTemplate.details || { type: 'review', formId: '' },
      audience: taskTemplate.audience || 'partner'
    }),
    [taskTemplate]
  )

  const [{ isPending: isUpdating }, { apiFunc: updateTaskTemplate }] = useMutation(
    grantProgramService.updateTaskTemplate,
    async () => {
      refresh()
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const handleClose = useCallback(() => {
    onClose()
    setModalState(null)
  }, [onClose])

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const payload = deleteNullValues({ ...values })

      if (taskTemplate.expiresAt && !values.expiresAt) {
        payload.expiresAt = null
      }

      updateTaskTemplate({
        grantProgramId: programId,
        taskTemplateId: taskTemplate.id,
        payload
      })
    },
    [taskTemplate, programId, updateTaskTemplate]
  )

  const formSelectOptions = useMemo(
    () =>
      forms.map(form => ({
        label: form.internalName || form.title || form.id,
        value: form.id
      })),
    [forms]
  )

  const renderModalContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('dialog.addTaskTemplate.success.title')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        >
          <Text size="small">{t('dialog.addTaskTemplate.success.text.templateCreated')}</Text>
        </AcknowledgeModal>
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('dialog.addTaskTemplate.error.title')}
          description={t('dialog.addTaskTemplate.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    return (
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {formik => {
          const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues)

          return (
            <ActionModal
              title={t('button.editTaskTemplate')}
              primaryButtonText={t('button.save')}
              secondaryButtonText={t('button.cancel')}
              handleClose={handleClose}
              handleSubmit={formik.submitForm}
              loading={isUpdating}
              disabled={!formik.isValid || !hasChanges || isUpdating}
              maxHeight="60vh"
              primaryBtnTestId="edit-task-template-submit-button"
              secondaryBtnTestId="edit-task-template-cancel-button"
            >
              <Flex direction="column" gap="lg">
                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('dialog.addTaskTemplate.form.name.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      data-testid="templateName"
                      necessity="required"
                    >
                      <TextInput
                        data-testid="templateName"
                        placeholder={t('dialog.addTaskTemplate.form.name.placeholder')}
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="description">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('dialog.addTaskTemplate.form.description.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      data-testid="templateDescription"
                      necessity="required"
                    >
                      <TextArea
                        data-testid="templateDescription"
                        placeholder={t('dialog.addTaskTemplate.form.description.placeholder')}
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        maxLength={255}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="details.type">
                  {({ field, meta, form }: FieldProps) => (
                    <FormField
                      label={t('dialog.addTaskTemplate.form.type.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      data-testid="templateType"
                      necessity="required"
                    >
                      <Select
                        name={field.name}
                        placeholder={t('dialog.addTaskTemplate.form.type.placeholder')}
                        options={templateTypes}
                        onChange={event => form.setFieldValue('details.type', event.value)}
                        defaultValue={templateTypes.find(tt => tt.value === field.value)}
                      />
                    </FormField>
                  )}
                </Field>

                {(formik.values.details?.type === 'review' || formik.values.details?.type === 'decision') && (
                  <>
                    <Divider my="md" />
                    <FormField label={t('dialog.addTaskTemplate.form.filterByPartner')} data-testid="formPartnerFilter">
                      <AsyncSelect
                        data-testid="formPartnerFilter"
                        placeholder={allFormsLabel}
                        options={partnerSelectOptions}
                        query={partnerSearchQuery}
                        setQuery={setPartnerSearchQuery}
                        loading={isPartnersLoading}
                        loadingText={t('dialog.addTaskTemplate.form.loading')}
                        noResultsFoundText={t('typography.noResultsFound')}
                        defaultValue={
                          selectedPartnerId
                            ? { value: selectedPartnerId, label: selectedPartnerLabel }
                            : { value: '', label: allFormsLabel }
                        }
                        onChange={e => {
                          const value = e?.value ?? ''
                          setSelectedPartnerId(value)
                          setSelectedPartnerLabel(e?.label ?? allFormsLabel)
                          formik.setFieldValue('details.formId', '')
                        }}
                      />
                    </FormField>
                    <Field name="details.formId">
                      {({ field, form }: FieldProps) => (
                        <FormField
                          label={t('dialog.addTaskTemplate.form.formId.label')}
                          data-testid="templateFormId"
                          necessity="required"
                        >
                          <Select
                            placeholder={
                              formsLoading
                                ? t('dialog.addTaskTemplate.form.loading')
                                : t('dialog.addTaskTemplate.form.formId.placeholder')
                            }
                            searchable
                            options={formSelectOptions}
                            onChange={event => form.setFieldValue('details.formId', event.value)}
                            defaultValue={formSelectOptions.find(f => f.value === field.value)}
                          />
                        </FormField>
                      )}
                    </Field>
                    {formik.values.details.formId && (
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => onPreviewForm(formik.values.details.formId!)}
                        data-testid="preview-form-button"
                      >
                        {t('grants.programDetails.taskTemplates.previewForm')}
                      </Button>
                    )}
                    <Divider my="md" />
                  </>
                )}

                {formik.values.details?.type === 'completion_event' && (
                  <Field name="details.completionEventName">
                    {({ field }: FieldProps<string>) => (
                      <FormField
                        label={t('dialog.addTaskTemplate.form.completionEvent.label')}
                        data-testid="templateCompletionEvent"
                        necessity="required"
                      >
                        <TextArea
                          data-testid="templateCompletionEvent"
                          placeholder={t('dialog.addTaskTemplate.form.completionEvent.placeholder')}
                          name={field.name}
                          value={field.value || ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          maxLength={255}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {deadlineType === 'dueByDate' && (
                  <Field name="dueByDate">
                    {({ field, meta, form }: FieldProps) => (
                      <FormField
                        label={t('dialog.addTaskTemplate.form.deadline.label')}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        statusMessage={meta.touched && meta.error ? (meta.error as string) : undefined}
                        data-testid="templateDeadline"
                        necessity="required"
                      >
                        <DateInput
                          name={field.name}
                          value={field.value ?? null}
                          onChange={date => {
                            if (date) {
                              form.setFieldValue('dueByDate', date)
                              form.setFieldValue('hoursUntilDue', null)
                            }
                          }}
                          dateFormat="DD MMM YYYY"
                          placeholder="DD / MM / YYYY"
                          data-testid="date-picker"
                          withinPopup
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {deadlineType === 'hoursUntilDue' && (
                  <Field name="hoursUntilDue">
                    {({ field, meta, form }: FieldProps) => (
                      <FormField
                        label={t('dialog.addTaskTemplate.form.hoursUntilDue.label')}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        statusMessage={meta.touched && meta.error ? (meta.error as string) : undefined}
                        data-testid="templateHoursUntilDue"
                        necessity="required"
                      >
                        <TextInput
                          type="number"
                          step={1}
                          min={0}
                          max={999999}
                          data-testid="templateHoursUntilDue"
                          name={field.name}
                          value={field.value || ''}
                          placeholder={t('dialog.addTaskTemplate.form.hoursUntilDue.placeholder')}
                          onBlur={field.onBlur}
                          onChange={e => {
                            const value = e.target.valueAsNumber
                            const hours = Number.isNaN(value) ? null : Math.floor(value)
                            form.setFieldValue('hoursUntilDue', hours)

                            if (hours !== null) {
                              form.setFieldValue('dueByDate', null)
                            }
                          }}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                <Field name="expiresAt">
                  {({ field, meta, form }: FieldProps) => (
                    <FormField
                      label={t('dialog.addTaskTemplate.form.expiresAt.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? (meta.error as string) : undefined}
                      data-testid="templateExpiresAt"
                    >
                      <DateInput
                        name={field.name}
                        value={field.value ?? null}
                        onChange={(date: Date | null) => {
                          form.setFieldValue('expiresAt', date && dayjs(date).isValid() ? dayjs(date).toDate() : null)
                        }}
                        onBlur={field.onBlur}
                        placeholder="DD / MM / YYYY"
                        dateFormat="DD MMM YYYY"
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        clearable
                        withinPopup
                        data-testid="expires-at-date-picker"
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="audience">
                  {({ field, meta, form }: FieldProps) => (
                    <FormField
                      label={t('dialog.addTaskTemplate.form.audience.label')}
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      data-testid="templateAudience"
                      necessity="required"
                    >
                      <Select
                        name={field.name}
                        placeholder={t('dialog.addTaskTemplate.form.audience.placeholder')}
                        options={audienceTypes}
                        onChange={event => form.setFieldValue('audience', event.value)}
                        defaultValue={audienceTypes.find(at => at.value === field.value)}
                      />
                    </FormField>
                  )}
                </Field>
              </Flex>
            </ActionModal>
          )
        }}
      </Formik>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="edit-task-template-modal" showModalOverflow={false}>
      {renderModalContent()}
    </Modal>
  )
}
